<script setup lang="ts">
import { Group, Image, init } from 'zrender'
import { getWatermark } from '@/charts'

const props = defineProps<{
    tile: Tile
}>()

/**
 * Use i18n.
 */
const { t } = useI18n()

/**
 * Use the query params.
 */
const { state } = useRouter()

/**
 * Use some dimension helpers.
 */
const { getDimensionColor, getDimension } = useTiles()

/**
 * Container for the title.
 */
const container = ref<HTMLDivElement>()

/**
 * Create the tile and get the svg string.
 */
const { svg, instance } = useTile(() => props.tile, container)

/**
 * Export instance.
 */
const exportInstance = init(document.createElement('div'))

/**
 * Export the images.
 */
const { menuItems } = useImageExport({
    instance: exportInstance,
    title: () => props.tile.title || t('title'),
    async getInstance() {
        const width = instance.getWidth()
        const height = instance.getHeight()
        const list = instance.storage.getDisplayList()
        exportInstance.clear()
        const padding = 20

        const fullHeight = height + padding * 2

        exportInstance.resize({
            width: width + padding * 2,
            height: fullHeight
        })

        const group = new Group({
            x: padding,
            y: padding
        })

        list.forEach(el => group.add(el))

        const { style } = await getWatermark()

        const watermark = new Image({
            z: -1,
            style: style,
            x: width - style.width,
            y: fullHeight - padding - style.height * 1.1
        })

        group.add(watermark)

        exportInstance.add(group)

        return exportInstance
    }
})

/**
 * Go to the tile view.
 */
function goToView() {
    const config = { ...props.tile.config }
    config.mode ??= 'DATA'
    return Object.assign(state, config)
}

const showViewButton = computed(() => {
    return props.tile.config && Object.keys(props.tile.config).length > 0
})
</script>
    
<template>
    <div class="relative bg-neutral">
        <div class="flex items-center gap-4 p-2">
            <div
                class="rounded-full px-3 py-1 text-xs uppercase"
                :style="
                    coloringStyle(tile.labelColor ?? getDimensionColor(tile))
                "
            >
                {{ tile.label ?? getDimension(tile)?.name }}
            </div>

            <div class="flex-1" />

            <UiDropdown
                :title="t('export-image')"
                :items="menuItems"
                direction="bottom"
            >
                <template #trigger>
                    <button
                        type="button"
                        class="transition-colors hover:text-light-green"
                    >
                        <PhotoIcon class="h-6" />
                    </button>
                </template>
            </UiDropdown>

            <UiButton v-if="showViewButton" small @click="goToView">
                {{ t('tile-view-button') }}
            </UiButton>
        </div>

        <div ref="container" class="p-4" v-html="svg" />
    </div>
</template>